import { AuthContext } from "@/features/authentication/AuthContext";
import { supabase } from "@/lib/supabase";
import type { Session, User } from "@supabase/supabase-js";
import { useEffect, useRef, useState, type ReactNode } from "react";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [session, setSession] = useState<Session | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    // null until the allowlist check resolves (or there's no session to check).
    const [approved, setApproved] = useState<boolean | null>(null);
    // The user id we've already run the approval check for. Supabase re-emits
    // auth events (SIGNED_IN / TOKEN_REFRESHED) on every tab focus; without this
    // guard the is_approved RPC would re-fire on each focus. Approval only needs
    // re-checking when the actual account changes, so we check once per user.
    const checkedUserId = useRef<string | null>(null);

    useEffect(() => {
        // Asks the allowlist (is_approved RPC, scoped to the caller's JWT)
        // whether this account has access. Fails closed: any error → not
        // approved, so the gate never opens on a transient hiccup.
        const checkApproval = async (): Promise<boolean> => {
            const { data, error } = await supabase.rpc("is_approved");
            if (error) {
                console.error("approval check failed:", error.message);
                return false;
            }
            return data === true;
        };

        const init = async () => {
            const { data } = await supabase.auth.getSession();
            if (data.session) {
                setSession(data.session);
                setUser(data.session.user);
                checkedUserId.current = data.session.user.id;
                setApproved(await checkApproval());
            }
            // Keep loading true until BOTH session and (when signed in) approval
            // resolve, so the guard doesn't flicker through the wrong branch.
            setLoading(false);
        };

        init();

        const { data: listener } = supabase.auth.onAuthStateChange(
            (_event, session) => {
                setSession(session);
                setUser(session?.user ?? null);
                if (session) {
                    // Only re-check when the account actually changes. Repeat
                    // events for the same user (token refresh on tab focus)
                    // leave the existing approval state untouched.
                    if (session.user.id !== checkedUserId.current) {
                        checkedUserId.current = session.user.id;
                        setApproved(null);
                        checkApproval().then(setApproved);
                    }
                } else {
                    checkedUserId.current = null;
                    setApproved(null);
                }
            }
        );

        return () => {
            listener.subscription.unsubscribe();
        };
    }, []);

    return (
        <AuthContext.Provider value={{ session, user, loading, approved }}>
            {children}
        </AuthContext.Provider>
    );
};
